import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ShoppingCart, Eye } from 'lucide-react';
import { cn, formatCurrency } from '@/lib/utils';
import BrandSymbol from '@/components/BrandSymbol';
import { useCartStore } from '@/stores/cart-store';
import { useUIStore } from '@/stores/ui-store';
import Badge from '@/components/ui/Badge';
import RatingStars from '@/components/ui/RatingStars';
import QuickEditWrapper from '@/components/quickedit/QuickEditWrapper';
import QuickViewModal from '@/components/QuickViewModal';
import ResponsiveImage from '@/components/ui/ResponsiveImage';

interface ProductCardProps {
  product: any;
  className?: string;
  showQuickAdd?: boolean;
}

export default function ProductCard({ product, className, showQuickAdd = true }: ProductCardProps) {
  const { t } = useTranslation('shop');
  const addItem = useCartStore((s) => s.addItem);
  const addToast = useUIStore((s) => s.addToast);
  const [isAdding, setIsAdding] = useState(false);
  const [showQuickView, setShowQuickView] = useState(false);

  const defaultVariant = product.variants?.[0];
  const price = defaultVariant?.price ?? product.base_price ?? product.price ?? 0;
  const thumbnailUrl = product.images?.[0]?.thumbnail_url ?? product.thumbnail_url;
  const fullUrl = product.images?.[0]?.url ?? product.image_url ?? product.primary_image_url;
  const imageUrl = thumbnailUrl || fullUrl;
  // The API attaches primary_image_srcset when imgproxy is configured for this
  // tenant. Falling back to the two baked variants keeps the card responsive on
  // a tenant that has not got the config yet, and on rows uploaded before it.
  const srcSet =
    (product.primary_image_srcset as string | undefined) ??
    (fullUrl && thumbnailUrl ? `${thumbnailUrl} 400w, ${fullUrl} 1200w` : null);

  async function handleAddToCart(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (!defaultVariant) return;
    setIsAdding(true);
    try {
      await addItem(defaultVariant.id);
      addToast(t('addedToCart'), 'success');
    } catch {
      addToast(t('addToCartFailed'), 'error');
    } finally {
      setIsAdding(false);
    }
  }

  return (
    // i18n-exempt: admin-only quick edit label, not visible to customers
    <QuickEditWrapper href={`/admin/products/${product.id}`} label={`Edit product: ${product.name}`}>
    <article className={cn('group', className)}>
      <Link
        to={`/product/${product.slug}`}
        className="block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600 rounded-lg"
        aria-label={product.name}
      >
        {/* Image */}
        <div className="relative aspect-[3/4] overflow-hidden rounded-lg bg-linen">
          {imageUrl ? (
            <ResponsiveImage
              image={{ url: imageUrl, srcSet }}
              sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
              alt={product.name}
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <BrandSymbol className="h-12 w-12 text-sage-300" />
            </div>
          )}
          {/* Warm overlay on hover */}
          <div className="absolute inset-0 bg-sage-900/0 group-hover:bg-sage-900/10 transition-colors duration-300" aria-hidden="true" />

          {/* Quick View button */}
          <button
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowQuickView(true); }}
            className={cn(
              'absolute bottom-3 left-3 flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-medium',
              'bg-cta-fg/90 text-cta hover:bg-cta-fg transition-all duration-200',
              'md:hover-reveal md:hover-reveal-slide',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600',
              'min-h-[36px]'
            )}
            aria-label={t('quickViewAriaLabel', { name: product.name })}
          >
            <Eye className="h-3.5 w-3.5" aria-hidden="true" />
            {t('quickView')}
          </button>

          {/* Add to cart button — visible on hover (desktop), always visible (mobile) */}
          {showQuickAdd && defaultVariant && (
            <button
              onClick={handleAddToCart}
              disabled={isAdding}
              aria-label={t('addToCartAriaLabel', { name: product.name })}
              aria-busy={isAdding}
              className={cn(
                'absolute bottom-3 right-3 flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-medium',
                'bg-cta text-cta-fg hover:bg-cta-deep transition-all duration-200',
                'md:hover-reveal md:hover-reveal-slide',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white',
                'disabled:opacity-50 min-h-[36px]'
              )}
            >
              <ShoppingCart className="h-3.5 w-3.5" aria-hidden="true" />
              {isAdding ? t('adding') : t('addToCart')}
            </button>
          )}
        </div>
      </Link>

      {/* Badges */}
      <div className="mt-2 flex flex-wrap gap-1.5">
        {product.is_organic && <Badge variant="organic">{t('badges.organic')}</Badge>}
        {product.is_heirloom && <Badge variant="heirloom">{t('badges.heirloom')}</Badge>}
        {product.plant_lifecycle && (
          <Badge variant="gray">{product.plant_lifecycle}</Badge>
        )}
      </div>

      {/* Info */}
      <div className="mt-2">
        {product.category_name && (
          <p className="text-xs uppercase tracking-wide-boutique text-sage-500">
            {product.category_name}
          </p>
        )}
        <Link
          to={`/product/${product.slug}`}
          className="block font-display text-base text-sage-900 hover:text-sage-700 transition-colors mt-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600 rounded"
        >
          <h3>{product.name}</h3>
        </Link>
        <p className="mt-1 font-sans text-lg font-semibold text-sage-800">
          {formatCurrency(price)}
        </p>
        {product.review_count > 0 && (
          <div className="flex items-center gap-1.5 mt-1">
            <RatingStars rating={parseFloat(product.avg_rating)} size="sm" />
            <span className="text-xs text-sage-500">({product.review_count})</span>
          </div>
        )}
      </div>
    </article>
    {showQuickView && (
      <QuickViewModal slug={product.slug} onClose={() => setShowQuickView(false)} />
    )}
    </QuickEditWrapper>
  );
}
